/**
 * A 20-line event bus between the fetch client and the auth layer.
 *
 * Why it exists: `api-client.ts` must be able to say "the session is gone"
 * without importing the zustand store or the router, and the store must be able
 * to react without importing the fetch client. A direct import either way
 * creates a cycle that Vite resolves at runtime as `undefined` — the classic
 * "store.getState is not a function on first load" bug.
 */

export type AuthEvent =
  /** A request came back 401 and the silent refresh could not save it. */
  | { type: 'session-expired' }
  /** A refresh succeeded — anything holding stale identity should re-read it. */
  | { type: 'session-refreshed' };

type Listener = (event: AuthEvent) => void;

const listeners = new Set<Listener>();

export function onAuthEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitAuthEvent(event: AuthEvent): void {
  for (const listener of [...listeners]) listener(event);
}

/** Test helper — leaks across test files otherwise. */
export function clearAuthEventListeners(): void {
  listeners.clear();
}
